/**
 * FoodPreferencesStep.js — Assessment Wizard, Screen 7 "Food Preferences"
 *
 * Captures diet type, cuisine, cooking time, and budget constraints used
 * to filter/rank the Nutrition Database. Built on the shared
 * AssessmentStepShell so the progress bar, step counter, question header,
 * and Previous/Next footer stay identical to every other wizard screen.
 *
 * Filter semantics (FSD v2.1 §6 Seven-Day Plan Definition — supersedes
 * the original FSD v1.0 wording that called Cooking Time and Budget
 * "hard filters"):
 *  - Diet Type    → HARD filter at the database query level (e.g. Jain
 *                   excludes root vegetables and onion/garlic). Governed
 *                   by the deterministic diet-safety engine; unaffected
 *                   by the v2.1 change below.
 *  - Cuisine      → SOFT preference, used to rank candidate meals, not
 *                   to exclude them — hence multi-select.
 *  - Cooking Time → SOFT ranking factor by default. "Budget and cooking
 *                   time are soft-ranking factors unless explicitly
 *                   configured as hard constraints" (§6).
 *  - Budget       → SOFT ranking factor by default, same §6 rule.
 *
 * The *_FILTER_MODE constants live in FoodPreferencesStep.constants.js
 * and ride along in the onNext payload so the Rule Engine doesn't have
 * to re-derive filter strictness from the field name alone.
 *
 * Cooking time and budget are PRE-ANSWERED (see SEEDED_DEFAULTS below).
 * Both controls always render a visible value — the <select> shows its
 * first option and the range thumb sits at BUDGET_DEFAULT_VISUAL_VALUE —
 * so leaving formData.cookingTime / formData.budget at null produced a
 * screen that looked fully answered while Next stayed disabled. We now
 * seed state to match what the controls display. Because both fields are
 * SOFT ranking factors under §6, an untouched default is a safe input to
 * the Rule Engine rather than a silent hard exclusion.
 *
 * Diet type and cuisine are still unseeded: they render no selection
 * until the user picks one, so Next remains disabled until those two
 * required fields are answered.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useFoodPreferencesAnalytics from "./useFoodPreferencesAnalytics";
import { validateFoodPreferences } from "./FoodPreferencesStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  DIET_TYPE_OPTIONS,
  CUISINE_OPTIONS,
  COOKING_TIME_OPTIONS,
  BUDGET_LEVELS,
  BUDGET_DEFAULT_VISUAL_VALUE,
  FOOD_PREFERENCES_INITIAL_DATA,
  DIET_TYPE_FILTER_MODE,
  CUISINE_FILTER_MODE,
  COOKING_TIME_FILTER_MODE,
  BUDGET_FILTER_MODE,
} from "./FoodPreferencesStep.constants";
import {
  FieldGroup,
  FieldLabel,
  RequiredMark,
  HelperValue,
  CardGrid,
  DietTypeCard,
  ChipGrid,
  Chip,
  Select,
  RangeInput,
  RangeScaleRow,
  RangeScaleItem,
} from "./FoodPreferencesStep.styles";

const STEP_NUMBER = STEP_NUMBERS.FOOD_PREFERENCES;

/**
 * The budget level the slider thumb visually rests on before any
 * interaction. BUDGET_DEFAULT_VISUAL_VALUE is a slider *value*, which is
 * not guaranteed to equal the level's array index — resolve by `value`
 * first and only fall back to positional lookup.
 */
const DEFAULT_BUDGET_LEVEL =
  BUDGET_LEVELS.find((level) => level.value === BUDGET_DEFAULT_VISUAL_VALUE) ??
  BUDGET_LEVELS[BUDGET_DEFAULT_VISUAL_VALUE] ??
  BUDGET_LEVELS[0] ??
  null;

/**
 * Values the controls already display on first paint. Seeding these keeps
 * formData in sync with what the user sees, so `nextDisabled` reflects the
 * screen's actual state.
 */
const SEEDED_DEFAULTS = {
  cookingTime: COOKING_TIME_OPTIONS[0]?.id ?? null,
  budget: DEFAULT_BUDGET_LEVEL?.id ?? null,
};

/**
 * Merge order: constants → seeded defaults → values restored from a
 * previous visit. `initialData` wins, but only for keys it actually
 * carries a value for — a stored `null` (e.g. from a session saved before
 * this change) falls back to the seeded default rather than resurrecting
 * the disabled-Next state.
 */
const buildInitialFormData = (initialData) => {
  const base = { ...FOOD_PREFERENCES_INITIAL_DATA };

  const merged = {
    ...base,
    cookingTime: base.cookingTime ?? SEEDED_DEFAULTS.cookingTime,
    budget: base.budget ?? SEEDED_DEFAULTS.budget,
  };

  if (!initialData) return merged;

  Object.entries(initialData).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      merged[key] = value;
    }
  });

  return merged;
};

/**
 * @param {object}   props
 * @param {string}   props.sessionId     - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData] - Previously entered values, if the
 *   user navigated back to this step.
 * @param {function} props.onPrevious    - Go to Screen 6 (Medical Information).
 * @param {function} props.onNext        - Go to Screen 8, receives the full
 *   food preferences object ({ dietType, cuisines, cookingTime, budget,
 *   dietTypeFilterMode, cuisineFilterMode, cookingTimeFilterMode,
 *   budgetFilterMode }). The *FilterMode fields carry the FSD v2.1 §6
 *   hard/soft classification so the candidate-pool query doesn't need a
 *   second copy of that decision.
 */
const FoodPreferencesStep = ({ sessionId, initialData = null, onPrevious, onNext }) => {
  const analytics = useFoodPreferencesAnalytics();
  const [formData, setFormData] = useState(() => buildInitialFormData(initialData));

  const { isValid, errors } = useMemo(() => validateFoodPreferences(formData), [formData]);

  const selectedBudgetLevel = useMemo(
    () => BUDGET_LEVELS.find((level) => level.id === formData.budget) ?? DEFAULT_BUDGET_LEVEL,
    [formData.budget]
  );

  const handleDietTypeSelect = useCallback(
    (dietTypeId) => {
      setFormData((prev) => ({ ...prev, dietType: dietTypeId }));
      analytics.trackDietTypeSelect(sessionId, dietTypeId);
    },
    [analytics, sessionId]
  );

  const handleCuisineToggle = useCallback(
    (cuisineId) => {
      const wasSelected = formData.cuisines.includes(cuisineId);
      setFormData((prev) => ({
        ...prev,
        cuisines: wasSelected
          ? prev.cuisines.filter((id) => id !== cuisineId)
          : [...prev.cuisines, cuisineId],
      }));
      analytics.trackCuisineToggle(sessionId, cuisineId, !wasSelected);
    },
    [analytics, sessionId, formData.cuisines]
  );

  const handleCookingTimeSelect = useCallback(
    (e) => {
      const value = e.target.value;
      if (!value) return;
      setFormData((prev) => ({ ...prev, cookingTime: value }));
      analytics.trackCookingTimeSelect(sessionId, value, COOKING_TIME_FILTER_MODE);
    },
    [analytics, sessionId]
  );

  const handleBudgetChange = useCallback(
    (e) => {
      const sliderValue = Number(e.target.value);
      // Resolve by the level's own `value` so the thumb position and the
      // stored level can't drift apart if BUDGET_LEVELS is ever reordered
      // or given non-sequential values.
      const level =
        BUDGET_LEVELS.find((lvl) => lvl.value === sliderValue) ?? BUDGET_LEVELS[sliderValue];
      if (!level) return;
      setFormData((prev) => ({ ...prev, budget: level.id }));
      analytics.trackBudgetChange(sessionId, level.id, BUDGET_FILTER_MODE);
    },
    [analytics, sessionId]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Food Preferences (Screen 7) returns to Medical
    // Information (Screen 6).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Next is already disabled via nextDisabled below,
      // this only fires if something calls onNext programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    // Attach the FSD v2.1 §6 filter-mode classification for each field
    // so downstream candidate-pool filtering reads it from this single
    // source rather than re-deriving hard/soft per field name.
    const payload = {
      ...formData,
      dietTypeFilterMode: DIET_TYPE_FILTER_MODE,
      cuisineFilterMode: CUISINE_FILTER_MODE,
      cookingTimeFilterMode: COOKING_TIME_FILTER_MODE,
      budgetFilterMode: BUDGET_FILTER_MODE,
    };

    analytics.trackStepNext(sessionId, payload);
    // Advances the wizard to Screen 8 — the wizard's step router decides
    // which screen renders next.
    onNext?.(payload);
  }, [analytics, sessionId, formData, isValid, errors, onNext]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Food preferences"
      description="This helps us pick the right recipes from our database for you."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel id="dietTypeLabel">
          Diet type
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <CardGrid role="radiogroup" aria-labelledby="dietTypeLabel">
          {DIET_TYPE_OPTIONS.map((opt) => {
            const active = opt.id === formData.dietType;
            return (
              <DietTypeCard
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={active}
                $active={active}
                onClick={() => handleDietTypeSelect(opt.id)}
              >
                {opt.label}
              </DietTypeCard>
            );
          })}
        </CardGrid>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel id="cuisineLabel">
          Cuisine
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <ChipGrid role="group" aria-labelledby="cuisineLabel">
          {CUISINE_OPTIONS.map((opt) => {
            const active = formData.cuisines.includes(opt.id);
            return (
              <Chip
                key={opt.id}
                type="button"
                aria-pressed={active}
                $active={active}
                onClick={() => handleCuisineToggle(opt.id)}
              >
                {opt.label}
              </Chip>
            );
          })}
        </ChipGrid>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor="cookingTime">
          Cooking time
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <Select
          id="cookingTime"
          value={formData.cookingTime ?? ""}
          onChange={handleCookingTimeSelect}
        >
          {COOKING_TIME_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </Select>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor="budget">
          Budget
          <RequiredMark>*</RequiredMark>
          {selectedBudgetLevel && (
            <>
              {" "}
              — <HelperValue>{selectedBudgetLevel.label}</HelperValue>
            </>
          )}
        </FieldLabel>
        <RangeInput
          id="budget"
          min={0}
          max={BUDGET_LEVELS.length - 1}
          step={1}
          value={selectedBudgetLevel ? selectedBudgetLevel.value : BUDGET_DEFAULT_VISUAL_VALUE}
          onChange={handleBudgetChange}
        />
        <RangeScaleRow>
          {BUDGET_LEVELS.map((level) => (
            <RangeScaleItem key={level.id} $active={formData.budget === level.id}>
              {level.label}
            </RangeScaleItem>
          ))}
        </RangeScaleRow>
      </FieldGroup>
    </AssessmentStepShell>
  );
};

FoodPreferencesStep.displayName = "FoodPreferencesStep";
export default React.memo(FoodPreferencesStep);